import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MatDialogRef } from '@angular/material/dialog'
import { CallerService } from '../../services/caller.service'
import { HttpResponse } from '@angular/common/http'
import { distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import { Store } from '@ngrx/store'
import { AppState } from '../../ngrx-store/app.state'
import { ppaAdded } from '../../ngrx-store/actions/corp-member.actions'
import { LgaPpa, PpaDirectoryService } from '../../services/ppa-directory.service'
import { Router } from '@angular/router'
import { SimilarPpa } from '../similar-ppas-dialog/similar-ppas-dialog.component'

interface ImagePreview {
    name: string
    dataUrl: string
    lastModified: number
    size: number
}

@Component({
    selector: 'app-add-new-place-dialog',
    templateUrl: './add-new-place-dialog.component.html',
    styleUrls: ['./add-new-place-dialog.component.css'],
})
export class AddNewPlaceDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('imgFileInput', { read: ElementRef<HTMLInputElement> })
        imgFileInput!: ElementRef<HTMLInputElement>

    ng_states: any[] = []

    ng_states_lgas: any[] = []

    saleImagesPreview: Array<ImagePreview> = []

    addingPPA: boolean = false

    /** The selected LGA's PPAs, for the name autocomplete (PpaDirectoryService) - they follow the LGA field. */
    PPAsInSelectedLGA: LgaPpa[] = []
    private ppasInSelectedLga$ = new BehaviorSubject<LgaPpa[]>([])
    filteredLgaPPAs: Observable<LgaPpa[]> | undefined;

    private destroy$ = new Subject<void>()

    filteredTypeOfInstitutionOptions: Observable<string[]> | undefined

    ppaAddressesSubject = new BehaviorSubject<string[]>([]);
    filteredPpaAddressesOptions: Observable<string[]> | undefined = this.ppaAddressesSubject.asObservable()

    constructor(
        private newPpaDialogRef: MatDialogRef<AddNewPlaceDialogComponent>,
        private callerService: CallerService,
        private store: Store<AppState>,
        private router: Router,
        private ppaDirectory: PpaDirectoryService,
    ) {}

    newPlaceForm: FormGroup = new FormGroup(
        {
            name: new FormControl(''),
            address: new FormControl(''),
            category: new FormControl(''),
            // TODO: should be disabled - should only be able to add in the state & lga they're assigned to.
            state_id: new FormControl('', [Validators.required]),
            state_lga_id: new FormControl(''),
        },
        [Validators.required],
    )

    ppaFormData: FormData = new FormData()

    close(): void {
        this.newPpaDialogRef.close()
    }

    ngAfterViewInit(): void {
        console.log('?', this.imgFileInput);
        
    }

    ngOnInit(): void {
        // must be 1st, so we can listen when we set the state id
        // TODO: we should either fetch all the states, and their lgas, and re-use that, instead of making this extra call
        this.newPlaceForm
            .get(['state_id'])
            ?.valueChanges.pipe(filter((_v) => !!_v)) // when the form resets, value is null, and we want to not make an api call.
            .subscribe({
                next: (value) => {
                    console.log('new state value', value)
                    this.callerService.fetchNigeriaStateLGAs(value).subscribe({
                        next: (res: HttpResponse<any>) => {
                            console.log('data', res)

                            this.ng_states_lgas = res.body?.lgas
                            // set the default corp member lga
                            // TODO: this might not be needed later
                            if (this.callerService.corpMember.lga_id) {
                                this.newPlaceForm
                                    .get('state_lga_id')
                                    ?.setValue(
                                        this.callerService.corpMember.lga_id,
                                    )
                            }
                        },
                        error: (err) => {
                            this.callerService.showNotification(
                                "Failed to load State's LGAs",
                            )
                        },
                    })
                },
            })

        // then this 2nd
        this.callerService.fetchAllNigeriaStates().subscribe({
            next: (res: HttpResponse<any>) => {
                console.log('data', res)

                this.ng_states = res.body?.states

                // set the default state of the corp member
                const _state_id = this.ng_states.find(
                    (state) =>
                        state.short_name ===
                        this.callerService.corpMember.state_short_name,
                )?.id
                if (_state_id) {
                    this.newPlaceForm.get('state_id')?.setValue(_state_id)
                }
            },
            error: (err) => {
                this.callerService.showNotification(
                    'Failed to load States data',
                )
            },
        })


        // The name suggestions are the selected LGA's PPAs (it starts as the corp member's own LGA).
        this.newPlaceForm
            .get('state_lga_id')
            ?.valueChanges.pipe(
                startWith(this.newPlaceForm.get('state_lga_id')?.value || this.callerService.corpMember.lga_id),
                filter((lgaId) => !!lgaId), // a reset's null keeps the list it had
                distinctUntilChanged(),
                switchMap((lgaId) => this.ppaDirectory.ppasInLga(lgaId).pipe(startWith([]))), // not the last LGA's while it loads
                takeUntil(this.destroy$),
            )
            .subscribe((ppas) => {
                this.PPAsInSelectedLGA = ppas
                this.ppasInSelectedLga$.next(ppas)
            })

        // Subscribed only while the panel is open (mat-autocomplete renders its options lazily) -
        // hence the subject above, which loads the list as soon as the form opens.
        this.filteredLgaPPAs = this.ppaDirectory.suggestions(this.ppasInSelectedLga$, this.newPlaceForm.get('name')!.valueChanges)

        this.filteredTypeOfInstitutionOptions = this.newPlaceForm.get(['category'])
        ?.valueChanges.pipe(
            startWith(''),
            map(value => this._filterTypeOfInstitutionOptions(value || '')),
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    populateFormData(): void {
        for (const field in this.newPlaceForm.controls) {
            // console.log(field, this.newPlaceForm.get(field))
            this.ppaFormData.set(field, this.newPlaceForm.get(field)?.value)
        }

        console.log('this.ppaFormData', this.ppaFormData)
    }

    private _filterTypeOfInstitutionOptions(value: string): string[] {
        return CallerService.PPA_TYPE_OPTIONS
        .filter((option: string) => option?.toLowerCase().includes(value.toLowerCase()));
    }

    /**
     * `confirmNew`: they've seen the PPAs like this one (the backend's 409 SIMILAR_PPAS) and theirs
     * is none of them.
     */
    postNewPlace(confirmNew = false): void {
        console.log('this.newPlaceForm.value', this.newPlaceForm.value)

        // TODO: convert to form data to allow upload of images/videos??

        if (!this.newPlaceForm.valid) {
            // todo?
            return
        }
        this.addingPPA = true
        this.populateFormData()
        this.ppaFormData.set('confirm_new', String(confirmNew))

        // TODO: check that this.accommodationPostFormData is valid
        this.callerService.addNewPPA(this.ppaFormData).subscribe({
            next: (res) => {
                console.log('res', res)
                // @todo: should we update the list of PPAs afterwards?
                if (res.status === 200) {
                    this.callerService.showNotification('PPA added!') // show link to the PPA? For them to also share in groups, PPA should have directions on how to find it??
                    // TODO: reset form properly?
                    this.clearFormDataAndImages()

                    if (res.body) {
                        try {
                            const addedPpa = JSON.parse(res.body)?.ppa
                            if (addedPpa) {
                                this.store.dispatch(ppaAdded({ ppa: addedPpa }))
                                this.ppaDirectory.add(addedPpa) // suggested from now on, like the LGA's other PPAs
                            }
                        } catch (error) {
                            console.log('Failed to parse response body:', error)
                        }
                    }
                } else {
                    // else show error message?
                }
                this.addingPPA = false
            },
            error: (err) => {
                console.log('err', err)
                this.addingPPA = false

                try {
                    const body = typeof err.error === 'string' ? JSON.parse(err.error) : err.error
                    if (body?.code === 'SIMILAR_PPAS') {
                        this.askWhichPpa(body.suggestions)
                        return
                    }
                    if (body?.message) {
                        this.callerService.showNotification(body.message)
                    }
                } catch (error) {
                    
                }
            },
        })
    }

    /** Theirs looks listed already: open the one they pick, or add theirs if it's none of them. */
    private askWhichPpa(suggestions: SimilarPpa[]): void {
        const name = this.newPlaceForm.get('name')?.value ?? ''
        this.callerService.askWhichPpa({ name, suggestions }).subscribe((result) => {
            if (!result) return
            if ('pick' in result) {
                this.newPpaDialogRef.close()
                this.callerService.showNotification(`${result.pick.name} is listed already - here it is.`)
                this.router.navigate(['/ppa', result.pick.slug_id])
            } else {
                this.postNewPlace(true)
            }
        })
    }

    /**
     * Also removes images.
     */
    clearFormDataAndImages(): void {
        this.ppaFormData = new FormData() // reset

        /**
         * reset the input form, so if you select the same file again it'll work
         * TODO: bug, imgFileInput is turning up null.
         */
        // this.imgFileInput.value = ''

        // reset the form after filling it - keeping the State and LGA, as the next PPA is likely in the same place
        const { state_id, state_lga_id } = this.newPlaceForm.value
        this.newPlaceForm.reset({ state_id, state_lga_id })
        for (const _control in this.newPlaceForm.controls) {
            this.newPlaceForm.controls[_control].setErrors(null)
        }

        this.saleImagesPreview = []
        this.ppaAddressesSubject.next([])
    }

    processImage(file: File) {
        const reader = new FileReader() // do we wanna define this every time the function is called?

        reader.onerror = function () {
            console.log('FileReader Err:', reader.error)
        }

        reader.onload = (e: any) => {
            console.log('e', e) // e.target.result
            this.saleImagesPreview.push({
                dataUrl: e.target.result,
                lastModified: file.lastModified,
                name: file.name,
                size: file.size,
            })
        }

        reader.readAsDataURL(file)
        this.ppaFormData.set(
            `ppa-media-${file.lastModified}-${file.size}-${file.name}`,
            file,
        ) // setting individually so it's easier to delete.
    }

    /**
     * TODO: Use the ViewChild instance instead of passing the file instance to this removePreviewImage() method.
     * do same in CreateNewAccommodation Component.
     * @param ifi
     * @param i
     */
    removePreviewImage(i: number) {
        this.saleImagesPreview.splice(i, 1)

        // clear the input. (incase the same file is deleted and selected again)
        this.imgFileInput.nativeElement.value = ''
    }

    onFileSelected(event: Event): void {
        console.log('evt', event)

        console.log('UI', (event.target as HTMLInputElement)?.files)
        const target = event.target as HTMLInputElement
        const files = target.files

        if (files && files.length) {
            for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
                this.processImage(files[fileIndex])
            }
        }
    }

    displayPpaName(value: any) {
        if (typeof value === 'object') {
            return value?.name
        }
        // else it's string
        return value
    }

    // @duplicated method
    // update the type of institution (and address?)
    ppaSelected(event: MatAutocompleteSelectedEvent) {
        console.log('ppa selected', event)

        const ppa = this.PPAsInSelectedLGA.find((ppaOption) => String(ppaOption.id) === String(event.option.id))
        console.log('selected ppa', ppa);
        if (ppa) {
            // @todo: also update the address??

            // update the type of ppa
            // show addresses ??
        }
        if (ppa?.type_of_ppa) {
            this.newPlaceForm.get('category')?.setValue(ppa?.type_of_ppa)
        }

        /**
         * if an address was selected and the ppa name is an existing name - disable submit button to prevent duplicate. also show a message too?
         */
        this.ppaAddressesSubject.next(
            ppa?.addresses ?? []
        )
        
    }

    saveDraft() {}
}
